import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { completeWalkthrough } from "../../api/auth";
import type { UserRole } from "../../api/types";
import { AppLayout } from "../../components/AppLayout";
import { ScreenState } from "../../components/ScreenState";
import { useSession } from "../../lib/session";
import { Walkthrough } from "../../screens/walkthrough/Walkthrough";

/*
 * Route guards. prepai had none, so any URL rendered for a signed-out visitor and failed on its first API call.
 * The server still enforces every rule; these only keep people on screens that make sense for them.
 */
export function RequireParticipant({
  children,
  needsTrack = true,
}: {
  children: ReactNode;
  needsTrack?: boolean;
}) {
  const { user, loading } = useSession();
  const location = useLocation();

  if (loading)
    return (
      <AppLayout>
        <ScreenState loading error={null}>
          {null}
        </ScreenState>
      </AppLayout>
    );
  if (!user) return <Navigate to="/" replace state={{ from: location.pathname }} />;
  if (user.role !== "Participant") return <Navigate to="/admin" replace />;
  if (needsTrack && !user.track) return <Navigate to="/track" replace />;
  // A brand-new participant takes the walkthrough once, after picking a track and before any step (gig or finance).
  if (location.pathname !== "/track" && user.tourRequired && !user.tourDoneAt)
    return <RequiredWalkthrough />;
  return <AppLayout>{children}</AppLayout>;
}

/* Finishing saves tourDoneAt; the fresh user from the server lifts the requirement and the route renders as asked. */
function RequiredWalkthrough() {
  const { setUser } = useSession();
  return (
    <Walkthrough
      required
      onFinish={async () => {
        setUser(await completeWalkthrough());
      }}
    />
  );
}

export function RequireAdmin({
  children,
  roles,
  allowPasswordChange = false,
}: {
  children: ReactNode;
  roles: UserRole[];
  /** The Account page stays open to an admin who must replace a temporary password; nothing else does. */
  allowPasswordChange?: boolean;
}) {
  const { user, loading } = useSession();

  if (loading)
    return (
      <div className="mx-auto max-w-[960px] p-8">
        <ScreenState loading error={null}>
          {null}
        </ScreenState>
      </div>
    );
  if (!user || user.role === "Participant") return <Navigate to="/admin" replace />;
  if (user.mustChangePassword && !allowPasswordChange)
    return <Navigate to="/admin/account" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/admin/home" replace />;
  // Admin screens draw their own workspace shell (AdminShell), not the participant column.
  return <>{children}</>;
}
